import { APP_BASE_HREF } from '@angular/common';
import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, ReplaySubject, combineLatest, from, of, race, timer } from 'rxjs';
import { catchError, filter, first, map, switchMap, take, tap } from 'rxjs/operators';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { UserData } from 'ish-core/models/user/user.interface';
import { ApiService } from 'ish-core/services/api/api.service';
import { getSsoRegistrationCancelled, getSsoRegistrationRegistered } from 'ish-core/store/customer/sso-registration';
import {
  getLoggedInCustomer,
  getUserAuthorized,
  getUserLoading,
  loadUserByAPIToken,
} from 'ish-core/store/customer/user';
import { ApiTokenService } from 'ish-core/utils/api-token/api-token.service';
import { InstanceCreators } from 'ish-core/utils/instance-creators';
import { whenTruthy } from 'ish-core/utils/operators';

import { IdentityProvider, IdentityProviderCapabilities, TriggerReturnType } from './identity-provider.interface';

export interface Auth0Config {
  type: 'auth0';
  domain: string;
  clientID: string;
}

function decodeState(state: string): string {
  try {
    if (state) {
      let decoded = state;
      let prev: string | null = null;
      while (decoded !== prev) {
        prev = decoded;
        decoded = decodeURIComponent(decoded);
      }
      if (decoded.startsWith('/')) {
        return decoded;
      }
    }
  } catch {}
  return '/account'; // default
}

@Injectable({ providedIn: 'root' })
export class Auth0IdentityProvider implements IdentityProvider {
  // emits true, when OAuth Service is successfully configured
  // used as an additional condition to check that the OAuth Service is configured before OAuth Service actions are used
  private oauthService: OAuthService;
  private discoveryReadySubject = new ReplaySubject<boolean>(1);
  discoveryReady$ = this.discoveryReadySubject.asObservable();
  constructor(
    private apiService: ApiService,
    private store: Store,
    private router: Router,
    private apiTokenService: ApiTokenService,
    parent: Injector,
    private accountFacade: AccountFacade,
    @Inject(APP_BASE_HREF) private baseHref: string
  ) {
    this.oauthService = InstanceCreators.getOAuthServiceInstance(parent);
  }

  getCapabilities(): IdentityProviderCapabilities {
    return {
      editPassword: false,
      editEmail: false,
      editProfile: false,
    };
  }

  init(config: Auth0Config) {
    if (SSR) {
      console.log('Auth0IdentityProvider: Skipping init() during SSR');
      return;
    }
    const effectiveOrigin = this.baseHref === '/' ? window.location.origin : window.location.origin + this.baseHref;
    const silentRefresh = `${window.location.origin}/assets/silent-refresh.html`;

    this.oauthService.configure({
      issuer: `https://${config.domain}/`,

      clientId: config.clientID,

      postLogoutRedirectUri: effectiveOrigin,

      scope: 'openid email profile offline_access',

      responseType: 'code',

      logoutUrl: `https://${config.domain}/v2/logout`,

      sessionChecksEnabled: true,
      silentRefreshRedirectUri: silentRefresh,
    });
    this.oauthService.loadDiscoveryDocument().then(() => {
      this.discoveryReadySubject.next(true);
    });

    this.apiTokenService
      .restore$(['user', 'order'])
      .pipe(
        switchMap(() => from(this.oauthService.loadDiscoveryDocumentAndTryLogin())),
        switchMap(() =>
          timer(0, 200).pipe(
            map(() => this.oauthService.getIdToken()),
            take(100),
            whenTruthy(),
            take(1)
          )
        ),
        whenTruthy(),
        switchMap(idToken => {
          if (!idToken) {
            return of(null);
          }
          // As soon as we have a token, do the immediate redirect handling
          this.handleImmediateRedirect();
          const inviteUserId = sessionStorage.getItem('invite-userid');
          const inviteHash = sessionStorage.getItem('invite-hash');
          return inviteUserId && inviteHash
            ? this.inviteRegistration(idToken, inviteUserId, inviteHash).pipe(
                tap(() => {
                  sessionStorage.removeItem('invite-userid');
                  sessionStorage.removeItem('invite-hash');
                })
              )
            : this.normalSignInRegistration(idToken);
        })
      )
      .subscribe(() => {
        this.apiTokenService.removeApiToken();
        localStorage.setItem('activeIdentityProvider', 'Auth0');
      });
  }

  /**
   * Redirect immediately once token is available.
   * Navigation occurs only after the store reports the user is authorized.
   */
  private handleImmediateRedirect() {
    const idToken = this.oauthService.getIdToken();
    if (!idToken) {
      return;
    }
    // Kick off background user load
    this.store.dispatch(loadUserByAPIToken({}));
  }

  triggerLogin(): TriggerReturnType {
    if (this.oauthService.hasValidIdToken()) {
      return of(true);
    }
    return this.discoveryReady$.pipe(
      take(1),
      switchMap(() => {
        this.oauthService.customQueryParams = {
          prompt: this.oauthService.hasValidAccessToken() ? 'none' : 'select_account',
        };

        return from(this.oauthService.initLoginFlowInPopup({ width: 500, height: 700 }));
      }),
      switchMap(() =>
        timer(0, 100).pipe(
          map(() => this.oauthService.getIdToken()),
          whenTruthy(),
          take(1)
        )
      ),
      tap(() => this.afterLogin()),
      map(() => false)
    );
  }

  private afterLogin(): void {
    if (!this.oauthService.hasValidIdToken()) {
      return;
    }

    this.store.pipe(select(getUserAuthorized), whenTruthy(), first()).subscribe(() => {
      const currentUrl = this.router.url;
      if (currentUrl === '/login' || currentUrl.startsWith('/login?')) {
        setTimeout(() => {
          this.router.navigateByUrl('/account');
        }, 0);
        return;
      }
      if (currentUrl === '/multiple-shipping-checkout' || currentUrl.startsWith('/multiple-shipping-checkout?')) {
        setTimeout(() => {
          this.router.navigateByUrl(currentUrl);
        }, 0);
        return;
      }

      const target = decodeState(currentUrl);
      this.router.navigateByUrl(target);
      this.discoveryReadySubject.next(false);
      this.discoveryReadySubject.complete();
    });
  }

  private normalSignInRegistration(idToken: string) {
    return this.apiService
      .post<UserData>('users/processtoken', {
        id_token: idToken,
        options: ['CREATE_USER'],
      })
      .pipe(
        tap(() => {
          this.store.dispatch(loadUserByAPIToken({}));
        }),
        switchMap((userData: UserData) =>
          combineLatest([this.store.pipe(select(getLoggedInCustomer)), this.store.pipe(select(getUserLoading))]).pipe(
            filter(([, loading]) => !loading),
            first(),
            switchMap(([customer]) =>
              !customer
                ? this.router.navigate(['/register', 'sso'], {
                    queryParams: {
                      userid: userData.businessPartnerNo,
                      firstName: userData.firstName,
                      lastName: userData.lastName,
                    },
                  })
                : of(false)
            ),
            switchMap((navigated: boolean) =>
              // eslint-disable-next-line unicorn/no-null
              navigated || navigated === null
                ? race(
                    this.store.pipe(
                      select(getSsoRegistrationRegistered),
                      whenTruthy(),
                      tap(() => {
                        this.store.dispatch(loadUserByAPIToken({}));
                      })
                    ),
                    this.store.pipe(
                      select(getSsoRegistrationCancelled),
                      whenTruthy(),
                      map(() => false),
                      tap(() => this.router.navigateByUrl('/logout'))
                    )
                  )
                : of(navigated)
            )
          )
        ),
        switchMap(() => this.store.pipe(select(getUserAuthorized), whenTruthy(), first())),
        catchError((error: HttpError) => {
          this.apiTokenService.removeApiToken();
          this.triggerLogout();
          return of(error);
        })
      );
  }

  private inviteRegistration(idToken: string, userId: string, hash: string) {
    return this.apiService
      .post<UserData>('users/processtoken', {
        id_token: idToken,
        secure_user_ref: {
          user_id: userId,
          secure_code: hash,
        },
        options: ['UPDATE'],
      })
      .pipe(
        tap(() => {
          this.store.dispatch(loadUserByAPIToken({}));
        }),
        switchMap(() => this.store.pipe(select(getUserAuthorized), whenTruthy(), first())),
        catchError((error: HttpError) => {
          this.apiTokenService.removeApiToken();
          this.triggerLogout();
          return of(error);
        })
      );
  }

  triggerRegister(route: ActivatedRouteSnapshot): TriggerReturnType {
    if (route.queryParamMap.get('userid')) {
      return of(true);
    } else {
      return this.oauthService.loadDiscoveryDocumentAndLogin({
        state: route.queryParams.returnUrl,
      });
    }
  }

  triggerInvite(route: ActivatedRouteSnapshot): TriggerReturnType {
    sessionStorage.setItem('invite-userid', route.queryParams.uid);
    sessionStorage.setItem('invite-hash', route.queryParams.Hash);
    return this.oauthService.loadDiscoveryDocumentAndLogin({
      state: route.queryParams.returnUrl,
    });
  }

  triggerLogout(): TriggerReturnType {
    localStorage.removeItem('activeIdentityProvider');
    this.apiTokenService.removeApiToken();
    this.accountFacade.logoutUser();

    try {
      if (this.oauthService.hasValidIdToken()) {
        this.oauthService.logOut(true);
      }
    } catch (error) {
      console.error('Google logout error:', error);
    }

    this.router.navigateByUrl('/home');
    return of(this.router.parseUrl('/home'));
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.oauthService.getIdToken()) {
      // fallback to standard handling
      return this.apiTokenService.intercept(req, next);
    }

    if (
      this.oauthService.getIdToken() &&
      !req.url.endsWith('users/processtoken') &&
      !req.headers.has(ApiService.TOKEN_HEADER_KEY)
    ) {
      let headers = req.headers;

      // move existing CAPTCHA authorization to X-Captcha-Token header
      const existingAuth = headers.get(ApiService.AUTHORIZATION_HEADER_KEY);
      if (existingAuth?.startsWith('CAPTCHA ')) {
        headers = headers.set('X-Captcha-Token', existingAuth).delete(ApiService.AUTHORIZATION_HEADER_KEY);
      }

      // set Bearer token as Authorization header
      headers = headers.set(ApiService.AUTHORIZATION_HEADER_KEY, `Bearer ${this.oauthService.getIdToken()}`);

      return next.handle(req.clone({ headers }));
    }

    return next.handle(req);
  }
}
